/**
 * FirstCloserCard — Early Adopter Program application form.
 *
 * Two states, driven by GET /api/early-adopter/stats:
 *
 * 1. **Seats remain (default)** — full application form. On submit:
 *      POST /api/early-adopter/apply → creates the account, grants 100
 *      credits, marks the user as a First Closer, records the cohort #,
 *      returns an auth token, auto-logs the user into /v3.
 *
 * 2. **Cap reached (100/100 claimed)** — swaps to a lightweight waitlist
 *      form (name + email only). On submit:
 *      POST /api/early-adopter/waitlist → records the lead, returns the
 *      waitlist position; UI thanks them.
 *
 * Reads attribution session from sessionStorage so the referring user gets
 * credited on conversion via the existing attribution loop.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { readStoredAttribution, clearStoredAttribution } from "@/hooks/useAttributionCapture";
import { KeyRound, Sparkles, CheckCircle2, ArrowRight, Loader2, ShieldCheck, Clock } from "lucide-react";

const ROLE_OPTIONS = [
  "Wholesaler",
  "Fix-and-Flip Investor",
  "Buy-and-Hold Investor",
  "Real Estate Agent",
  "Coach / Mentor",
  "Other",
];
const VOLUME_OPTIONS = ["0 (just starting)", "1-2 / month", "3-5 / month", "6-10 / month", "10+ / month"];

const CAP = 100;

export default function FirstCloserCard() {
  const nav = useNavigate();
  const { applySession } = useAuth();

  // Seats snapshot — drives whether to show apply form vs waitlist form.
  const [seats, setSeats] = useState({
    total_first_closers: 0,
    cap: CAP,
    seats_left: CAP,
    cap_reached: false,
  });
  const capReached = seats.cap_reached;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/early-adopter/stats");
        if (!cancelled && data) setSeats(data);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (capReached) {
    return <WaitlistCard seats={seats} />;
  }
  return (
    <ApplyCard
      seats={seats}
      onSeatsChange={setSeats}
      applySession={applySession}
      nav={nav}
    />
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Full apply form — seats 1–100                                          */
/* ────────────────────────────────────────────────────────────────────── */

function ApplyCard({ seats, onSeatsChange, applySession, nav }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: ROLE_OPTIONS[0],
    monthly_deal_volume: VOLUME_OPTIONS[1],
    biggest_pain: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.email.includes("@") || form.password.length < 8) {
      toast.error("Enter a valid email and a password of at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const attribution = readStoredAttribution();
      const payload = {
        ...form,
        tos_agreed: true,
        attribution_session: attribution?.session_token || null,
      };
      const { data } = await api.post("/early-adopter/apply", payload);
      try { localStorage.setItem("af_token", data.access_token); } catch { /* silent */ }
      if (typeof applySession === "function") applySession(data.user, data.access_token);
      clearStoredAttribution();
      const cohort = data.first_closer_cohort ?? data.founding_cohort;
      toast.success(
        `Welcome, First Closer #${cohort} — ${data.credits_granted} credits are already in your account.`
      );
      nav("/v3", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Cap reached mid-submit — re-fetch stats so the UI flips to waitlist.
      if (status === 423) {
        toast.error("All 100 First Closer seats just filled. Switching you to the waitlist…");
        try {
          const { data: fresh } = await api.get("/early-adopter/stats");
          if (fresh) onSeatsChange(fresh);
        } catch { /* silent */ }
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Application failed. Please try again.");
      }
    } finally { setBusy(false); }
  };

  const total = seats.total_first_closers ?? 0;
  const left  = seats.seats_left ?? CAP;

  return (
    <section
      data-testid="first-closer-card"
      className="relative overflow-hidden rounded-2xl border-2 border-amber-300/60 shadow-xl bg-white"
    >
      <div className="absolute inset-0 pointer-events-none opacity-70"
           style={{ backgroundImage:
             "radial-gradient(circle at 0% 0%, rgba(245,158,11,0.25), transparent 45%), " +
             "radial-gradient(circle at 100% 100%, rgba(139,92,246,0.20), transparent 55%)" }} />

      <div className="relative p-6 md:p-8">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 rounded-full text-[10px] font-black tracking-widest uppercase px-2.5 py-1 text-white"
                style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #8B5CF6)" }}>
            <KeyRound className="w-3 h-3" /> First Closer
          </span>
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest"
                data-testid="first-closer-counter">
            {total} / {CAP} claimed · {left} left
          </span>
        </div>

        {/* Progress bar — visual scarcity */}
        <div className="mt-3 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden"
             data-testid="first-closer-progress">
          <div className="h-full rounded-full transition-[width] duration-700"
               style={{
                 width: `${Math.min(100, Math.round((total / CAP) * 100))}%`,
                 backgroundImage: "linear-gradient(90deg, #F59E0B, #8B5CF6)",
               }} />
        </div>

        <h2 className="mt-4 text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
          Reserve your Priority-Access spot.
        </h2>
        <p className="mt-2 text-[14px] text-slate-600 leading-relaxed max-w-lg">
          First Closer claims are <b>paused</b> while Tracy and a small partner
          cohort run the platform through real deals. Drop your email below —
          you'll be first in line when we open the next wave, with every First
          Closer perk still on the table.
        </p>

        <ul className="mt-4 space-y-1.5 text-[13px] text-slate-700">
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><b>100 free lead credits</b> on signup — worth ~$300 in Marketplace value</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><b>40% off any paid plan, for life</b> — locked in the moment you apply</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><b>First Closer badge</b> on your dashboard + every share card you post</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><b>Direct feedback line</b> — shape the roadmap before public launch</li>
        </ul>

        <form onSubmit={submit} className="mt-5 space-y-2.5" data-testid="first-closer-form">
          <div className="grid md:grid-cols-2 gap-2.5">
            <input type="text" value={form.name} onChange={set("name")} required placeholder="Your full name"
                   data-testid="fc-input-name"
                   className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <input type="email" value={form.email} onChange={set("email")} required placeholder="Work email"
                   data-testid="fc-input-email"
                   className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <input type="password" value={form.password} onChange={set("password")} required placeholder="Create a password (8+ characters)"
                 data-testid="fc-input-password"
                 className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          <div className="grid md:grid-cols-2 gap-2.5">
            <select value={form.role} onChange={set("role")} data-testid="fc-select-role"
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={form.monthly_deal_volume} onChange={set("monthly_deal_volume")} data-testid="fc-select-volume"
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
              {VOLUME_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <textarea value={form.biggest_pain} onChange={set("biggest_pain")} rows={2}
                    placeholder="What's your biggest challenge in real estate right now? (optional but appreciated)"
                    data-testid="fc-input-pain"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
          <button type="submit" disabled={busy}
                  data-testid="fc-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg text-white text-[14px] font-bold px-4 py-3 shadow-md disabled:opacity-60"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B 0%, #EA580C 60%, #8B5CF6 100%)" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Apply — Get Instant Access
            <ArrowRight className="w-4 h-4" />
          </button>
          <div className="text-[10.5px] text-slate-400 text-center">
            By applying you agree to our Terms of Service. Cancel anytime.
          </div>
        </form>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*  Waitlist form — seat 101+                                              */
/* ────────────────────────────────────────────────────────────────────── */

function WaitlistCard({ seats }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { position, total_on_list, already_on_list }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const attribution = readStoredAttribution();
      const { data } = await api.post("/early-adopter/waitlist", {
        name: form.name.trim(),
        email: form.email.trim(),
        attribution_session: attribution?.session_token || null,
      });
      clearStoredAttribution();
      setSubmitted(data);
      toast.success(
        data.already_on_list
          ? `You're already on the list — position #${data.position}.`
          : `You're on the list — position #${data.position}.`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't join the waitlist. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <section
      data-testid="first-closer-waitlist-card"
      className="relative overflow-hidden rounded-2xl border-2 border-amber-300/60 shadow-xl bg-white"
    >
      <div className="absolute inset-0 pointer-events-none opacity-70"
           style={{ backgroundImage:
             "radial-gradient(circle at 0% 0%, rgba(245,158,11,0.25), transparent 45%), " +
             "radial-gradient(circle at 100% 100%, rgba(139,92,246,0.20), transparent 55%)" }} />

      <div className="relative p-6 md:p-8">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 rounded-full text-[10px] font-black tracking-widest uppercase px-2.5 py-1 text-white"
                style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #8B5CF6)" }}>
            <Clock className="w-3 h-3" /> Waitlist open
          </span>
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest"
                data-testid="first-closer-cap-counter">
            {CAP} / {CAP} seats claimed
          </span>
        </div>

        {/* Full progress bar */}
        <div className="mt-3 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full rounded-full"
               style={{
                 width: "100%",
                 backgroundImage: "linear-gradient(90deg, #F59E0B, #8B5CF6)",
               }} />
        </div>

        <h2 className="mt-4 text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
          {submitted ? "You're in line." : "All 100 First Closer seats are claimed."}
        </h2>

        {submitted ? (
          <>
            <p className="mt-2 text-[14px] text-slate-600 leading-relaxed max-w-lg">
              You&rsquo;re position <b className="text-slate-900">#{submitted.position}</b> on the
              First Closer waitlist ({submitted.total_on_list} in line). If any seats free up
              before launch, or the moment we open a second cohort, you&rsquo;ll be first through
              the door.
            </p>
            <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3"
                 data-testid="first-closer-waitlist-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-[13px] font-bold text-slate-900">Confirmed — {form.email}</div>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-600">
                  We&rsquo;ll email you the moment a seat opens. No spam. No sharing.
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-[14px] text-slate-600 leading-relaxed max-w-lg">
              The priority-access cohort is full — but the door isn&rsquo;t closed. Join the waitlist
              and we&rsquo;ll pull you in the moment a seat frees up, or when we open a second
              wave.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-2.5" data-testid="first-closer-waitlist-form">
              <input type="text" value={form.name} onChange={set("name")} required placeholder="Your full name"
                     data-testid="fc-waitlist-input-name"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              <input type="email" value={form.email} onChange={set("email")} required placeholder="Work email"
                     data-testid="fc-waitlist-input-email"
                     className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              <button type="submit" disabled={busy}
                      data-testid="fc-waitlist-submit-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg text-white text-[14px] font-bold px-4 py-3 shadow-md disabled:opacity-60"
                      style={{ backgroundImage: "linear-gradient(135deg, #F59E0B 0%, #EA580C 60%, #8B5CF6 100%)" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                Join the Waitlist
                <ArrowRight className="w-4 h-4" />
              </button>
              <div className="text-[10.5px] text-slate-400 text-center inline-flex items-center gap-1 justify-center w-full">
                <ShieldCheck className="w-3 h-3" /> One email when a seat opens. Never shared.
              </div>
            </form>
          </>
        )}

        {/* Legacy alias kept in the DOM in case something outside targets it. */}
        <span data-testid="first-closer-counter" className="sr-only">
          {seats.total_first_closers ?? CAP} / {CAP} claimed · 0 left
        </span>
      </div>
    </section>
  );
}
